// Per-section one-line highlight strings for the collapsed-section headers.
// Replaces the LLM-generated preview bullets after the AI strip-down. Pure
// derivation from the stats object — no async, no API calls, deterministic
// across recap loads.
//
// Each highlight function returns a SHORT string (≤ 80 chars ideal) or null
// when there isn't enough data to say anything interesting. Null suppresses
// the preview line entirely in the collapsed-section header.
//
// Schema-aware where the median-wins format applies: career/season highlights
// use total* fields when present (identity-backfilled to w/l when not).

function fmtPct(p) { return `${(p * 100).toFixed(1)}%`; }
function fmt1(n) { return Number(n).toFixed(1); }

export function pickSectionHighlight(section, stats) {
  if (!stats) return null;
  const fn = HIGHLIGHTS[section];
  return fn ? fn(stats) : null;
}

const HIGHLIGHTS = {
  championships(stats) {
    const counts = stats.championships?.titleCounts || {};
    const entries = Object.entries(counts).map(([owner, years]) => ({ owner, count: years.length }));
    if (!entries.length) return null;
    entries.sort((a, b) => b.count - a.count);
    const winnerCount = entries.length;
    const top = entries[0];
    return `${winnerCount} different winner${winnerCount === 1 ? "" : "s"} · ${top.owner} leads with ${top.count} title${top.count === 1 ? "" : "s"}`;
  },

  drafts(stats) {
    const drafts = stats.drafts || [];
    if (!drafts.length) return null;
    const startup = drafts.filter(d => d.draftEventType === "startup").length;
    const rookie  = drafts.filter(d => d.draftEventType === "rookie").length;
    const annual  = drafts.filter(d => d.draftEventType === "annual").length;
    const parts = [];
    if (startup) parts.push(`${startup} startup`);
    if (rookie)  parts.push(`${rookie} rookie draft${rookie === 1 ? "" : "s"}`);
    if (annual)  parts.push(`${annual} annual draft${annual === 1 ? "" : "s"}`);
    return `${drafts.length} draft${drafts.length === 1 ? "" : "s"} archived · ${parts.join(" + ")}`;
  },

  keepers(stats) {
    const byOwner = {};
    let total = 0;
    for (const d of stats.drafts || []) {
      for (const p of d.picks || []) {
        if (!p?.isKeeper) continue;
        const o = p.owner || "—";
        byOwner[o] = (byOwner[o] || 0) + 1;
        total++;
      }
    }
    if (!total) return null;
    const top = Object.entries(byOwner).sort((a, b) => b[1] - a[1])[0];
    return `${total} keeper${total === 1 ? "" : "s"} · ${top[0]} kept the most (${top[1]})`;
  },

  trades(stats) {
    const meta = stats.tradeMeta;
    if (!meta || !meta.totalTrades) return null;
    const tp = meta.topPair;
    if (tp) {
      return `${meta.totalTrades} trade${meta.totalTrades === 1 ? "" : "s"} across ${meta.seasonsWithTrades} season${meta.seasonsWithTrades === 1 ? "" : "s"} · ${tp.ownerA} & ${tp.ownerB} trade most (${tp.count} deal${tp.count === 1 ? "" : "s"})`;
    }
    return `${meta.totalTrades} trade${meta.totalTrades === 1 ? "" : "s"} across ${meta.seasonsWithTrades} season${meta.seasonsWithTrades === 1 ? "" : "s"}`;
  },

  careerTotals(stats) {
    const totals = stats.careerTotals || [];
    if (!totals.length) return null;
    const ranked = totals.length;
    // careerTotals is sorted by totalW desc; pull the top winPct (using
    // totalWinPct when medians apply, falls through to winPct otherwise).
    const sortedByPct = [...totals].sort((a, b) => (b.totalWinPct ?? b.winPct) - (a.totalWinPct ?? a.winPct));
    const leader = sortedByPct[0];
    if (!leader) return `${ranked} owners ranked`;
    const pct = leader.totalWinPct ?? leader.winPct;
    return `${ranked} owner${ranked === 1 ? "" : "s"} ranked · ${leader.owner} leads at ${fmtPct(pct)} win%`;
  },

  singleGame(stats) {
    const top = stats.singleGame?.highest?.[0];
    if (!top) return null;
    return `Highest: ${fmt1(top.pts)} (${top.owner}, ${top.year} Wk${top.week})`;
  },

  streaks(stats) {
    const top = stats.streaks?.longestWinStreaks?.find(s => s.length > 0);
    if (!top) return null;
    return `Longest win streak: ${top.length} (${top.owner}${top.startedAt ? `, ${top.startedAt}` : ""})`;
  },

  singleSeason(stats) {
    const best = stats.singleSeason?.bestRecords?.[0];
    if (!best) return null;
    const w = best.totalW ?? best.w;
    const l = best.totalL ?? best.l;
    return `Best record: ${w}-${l} (${best.owner} ${best.year})`;
  },

  // formatHistory lives off pipelineState.seasons + stats.drafts so the
  // helper can't derive it from `stats` alone - return null and let the
  // count chip ("N years of changes") carry the preview info.
  formatHistory() { return null; },

  pointsPerGame(stats) {
    const careers = (stats.careerTotals || []).filter(r => (r.games || 0) > 0);
    if (!careers.length) return null;
    const top = [...careers].sort((a, b) => (b.ppg || 0) - (a.ppg || 0))[0];
    const topSeason = stats.singleSeason?.highestPPG?.[0];
    const lead = `Career: ${top.owner} ${fmt1(top.ppg)}`;
    if (!topSeason) return lead;
    return `${lead} · Best season: ${topSeason.owner} ${fmt1(topSeason.ppg)} (${topSeason.year})`;
  },

  headToHead(stats) {
    const top = stats.headToHead?.lopsidedRivalries?.[0];
    if (!top) return null;
    return `Most lopsided: ${top.winner} over ${top.loser}, ${top.w}-${top.l} (${fmtPct(top.winPct)})`;
  },

  playoffs(stats) {
    const top = stats.playoffs?.careerRecords?.[0];
    if (!top) return null;
    return `Best playoff record: ${top.owner} ${top.w}-${top.l} (${(top.winPct).toFixed(3).replace(/^0/, "")})`;
  },

  luck(stats) {
    // Pick the single most extreme luck swing — could be +luckiest or -unluckiest.
    const lucky = stats.luck?.luckiestSeasons?.[0];
    const unlucky = stats.luck?.unluckiestSeasons?.[0];
    if (!lucky && !unlucky) return null;
    const luckyMag = lucky ? Math.abs(lucky.luck) : 0;
    const unluckyMag = unlucky ? Math.abs(unlucky.luck) : 0;
    if (unluckyMag >= luckyMag && unlucky) {
      return `Unluckiest season: ${unlucky.owner} ${unlucky.year} (${(unlucky.luck * 100).toFixed(1)}%)`;
    }
    if (lucky) {
      return `Luckiest season: ${lucky.owner} ${lucky.year} (+${(lucky.luck * 100).toFixed(1)}%)`;
    }
    return null;
  },

  volatility(stats) {
    const bb = stats.volatility?.boomBust || [];
    if (!bb.length) return null;
    // boomBust is sorted by stdDev desc → most volatile first. We want most
    // CONSISTENT (smallest CoV), so re-sort.
    const consistent = [...bb].filter(r => r.coefVar != null).sort((a, b) => a.coefVar - b.coefVar)[0];
    if (!consistent) return null;
    return `Most consistent: ${consistent.owner} (${fmtPct(consistent.coefVar)} CoV)`;
  },

  yoySwings(stats) {
    const fall = stats.yoySwings?.biggestFalls?.[0];
    if (!fall) return null;
    return `Biggest collapse: ${fall.owner} ${fall.year} (${(fall.swing * 100).toFixed(1)}%)`;
  },

  awards(stats) {
    const pb = stats.awards?.punchingBag || [];
    if (!pb.length) return null;
    const top = pb[0];
    return `Most blowout losses: ${top.owner} (${top.blowoutLosses})`;
  },
};

// Public list of sections that have a defined highlight function. Useful
// for callers iterating known-supported sections.
export const HIGHLIGHTED_SECTIONS = Object.keys(HIGHLIGHTS);
